import {Injectable} from '@angular/core';
import {forkJoin, Observable, of} from 'rxjs';
import {catchError, map} from 'rxjs/operators';
import {GoogleScraperService} from '../../../../shared/services/google-scraper.service';
import {SequenceService} from '../../collect-review/services/sequence.service';
import {CustomerService} from '../../collect-review/services/customer.service';
import {ReviewInfo} from '../../../../shared/models/review-info';
import {CustomerResList} from '../../collect-review/models/customer';
import {ReputationAlert} from '../models/reputation-dashboard.models';

interface OldCustomerCountResponse {
    oldCustomerCount: number;
}

@Injectable({
    providedIn: 'root'
})
export class ReputationAlertsService {

    constructor(
        private googleScraperService: GoogleScraperService,
        private sequenceService: SequenceService,
        private customerService: CustomerService
    ) {
    }

    getAlerts(locationId: string, inProgressCount = 0): Observable<ReputationAlert[]> {
        return forkJoin({
            googleReviews: this.googleScraperService.getReviewInformation(locationId, 50, 0).pipe(
                catchError(() => of({reviews: [] as ReviewInfo[], totalElements: 0}))
            ),
            lowRatingCustomers: this.customerService.getAllCustomer(locationId, '', 0).pipe(
                catchError(() => of({customerResList: [] as CustomerResList[]}))
            ),
            noOutcome: this.sequenceService.getOldCustomerInfo(locationId, 'NoOutcome', 1).pipe(
                catchError(() => of({oldCustomerCount: 0} as OldCustomerCountResponse))
            )
        }).pipe(
            map(({googleReviews, lowRatingCustomers, noOutcome}) => {
                const noOutcomeResponse = noOutcome as OldCustomerCountResponse;
                const reviews = googleReviews.reviews || [];
                const awaitingReply = reviews.filter(r => !r.reviewResponse?.trim()).length;
                const lowGoogleRatings = reviews.filter(r => r.rating <= 2).length;
                const lowCustomerFeedback = (lowRatingCustomers.customerResList || []).filter(customer =>
                    (customer.responseData || []).some(response => response.rating <= 2)
                ).length;
                const lowRatingCount = lowGoogleRatings + lowCustomerFeedback;
                const noResponseCount = Math.max(
                    noOutcomeResponse?.oldCustomerCount ?? 0,
                    inProgressCount
                );

                const alerts: ReputationAlert[] = [
                    {
                        id: 'low-rating',
                        label: 'Low rating feedback',
                        description: 'Customers who left a low private or Google rating',
                        count: lowRatingCount,
                        severity: 'red',
                        route: '/analytics/review',
                        icon: 'heroicons_outline:exclamation-triangle'
                    },
                    {
                        id: 'awaiting-reply',
                        label: 'Google reviews awaiting reply',
                        description: 'Public Google reviews without a business reply',
                        count: awaitingReply,
                        severity: 'orange',
                        route: '/monitor-reviews',
                        icon: 'heroicons_outline:chat-alt-2'
                    },
                    {
                        id: 'no-response',
                        label: 'Enrolled customers with no response',
                        description: 'Customers still enrolled without feedback or outcome',
                        count: noResponseCount,
                        severity: 'blue',
                        route: '/collect-review/customer',
                        icon: 'heroicons_outline:user-group'
                    }
                ];
                return alerts.filter(alert => alert.count > 0);
            })
        );
    }
}
